import { createSelector } from 'reselect';

import config from '~/config';
import { AppData, CharacterData, QueryResponseData, ShortCharacterContract } from '../typings';

const BASE_PATHNAME = config.basePathname;

const getCharacter = (state: CharacterData) => state || {};

const getId = createSelector([getCharacter], ({ id }) => id);

// Username is added by MyCharactersListing since it comes from the JWT, not the API
const getUsername = createSelector([getCharacter], ({ userName }) => userName);

const getName = createSelector([getCharacter], ({ name }) => name || 'Hero With No Name');

const getLevel = createSelector([getCharacter], ({ level }) => level);

const getStatus = createSelector([getCharacter], ({ status }) => status);

const getStatusSlug = createSelector([getCharacter], ({ statusSlug }) => statusSlug);

const getClassDescription = createSelector([getCharacter], ({ classDescription }) => classDescription);

const getRaceName = createSelector([getCharacter], ({ raceName }) => raceName);

const getSecondaryInfo = createSelector([getCharacter], ({ characterSecondaryInfo }) => characterSecondaryInfo);

const getAvatarUrl = createSelector([getCharacter], ({ avatarUrl }) => avatarUrl);

const getBackdropUrl = createSelector([getCharacter], ({ backdropUrl }) => backdropUrl);

const getCoverImageUrl = createSelector([getCharacter], ({ coverImageUrl }) => coverImageUrl);

const getCreatedDate = createSelector([getCharacter], ({ createdDate }) => createdDate);

const getLastModifiedDate = createSelector([getCharacter], ({ lastModifiedDate }) => lastModifiedDate);

const getIsAssigned = createSelector([getCharacter], ({ isAssigned }) => isAssigned);

const getCampaignId = createSelector([getCharacter], ({ campaignId }) => campaignId);

const isInCampaign = createSelector([getCampaignId], Boolean);

const getCampaignName = createSelector([getCharacter], ({ campaignName }) => campaignName);

const getCampaignDetailsLink = createSelector([getCampaignId], (id) => `${config.ddbBaseUrl}/campaigns/${id}`);

const getDetailsLink = createSelector([getUsername, getId], (username, id) => `${BASE_PATHNAME}/${id}`);

const getEditLink = createSelector([getUsername, getId], (username, id) => `${BASE_PATHNAME}/${id}/builder`);

const getSearchableTerms = createSelector(
    [isInCampaign, getLevel, getName, getClassDescription, getRaceName, getCampaignName],
    (isInCampaign, level, name, classDescription, raceName, campaignName) =>
        [
            isInCampaign && 'campaign',
            level.toString(),
            `level ${level}`,
            `lvl ${level}`,
            name,
            classDescription,
            raceName,
            campaignName,
        ].filter(Boolean),
);

// Analytics labels to use if a match is found in getSearchableTerms (by index)
const getSearchableTermsAnalyticsLabel = createSelector([isInCampaign, getLevel], (isInCampaign, level) =>
    [
        isInCampaign && 'campaign',
        '# (level)',
        `level #`,
        `lvl #`,
        'name',
        'class description',
        'race name',
        'campaign name',
    ].filter(Boolean),
);

function generateCharacter(character: ShortCharacterContract, userName: string | null): CharacterData {
    return {
        ...character,
        userName,
    };
}

function generateAppData(userName: string | null, data?: QueryResponseData): AppData {
    const characters = data?.data?.characters || [];

    return {
        characters: characters.map((character) => generateCharacter(character, userName)),
        maxCharacterSlotsAllowed: data?.data?.characterSlotLimit || null,
        hasLockedCharacters: data?.data?.canUnlockCharacters || false,
    };
}

export default {
    generateAppData,
    generateCharacter,
    getId,
    getName,
    getLevel,
    getStatus,
    getStatusSlug,
    getClassDescription,
    getRaceName,
    getSecondaryInfo,
    getAvatarUrl,
    getBackdropUrl,
    getCoverImageUrl,
    getCreatedDate,
    getLastModifiedDate,
    getIsAssigned,
    isInCampaign,
    getCampaignId,
    getCampaignName,
    getCampaignDetailsLink,
    getDetailsLink,
    getEditLink,
    getSearchableTerms,
    getSearchableTermsAnalyticsLabel,
};
